"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Badge from "@/components/ui/badge/Badge";
import Button from "@/components/ui/button/Button";
import DrawerPanel from "@/components/ui/DrawerPanel";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import { Modal } from "@/components/ui/modal/index";
import { rmcClient } from "@/lib/rmcClient";
import { gstSchema } from "@/lib/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";

const PREVIEW_SITES = 4;

const emptyPartyForm = () => ({
  name: "",
  gst_number: "",
  address: "",
  contact_no: "",
  is_active: true,
});

const emptySiteForm = () => ({
  name: "",
  address: "",
  contact: "",
  is_active: true,
});

function siteContact(s) {
  if (!s || typeof s !== "object") return "";
  return s.contact ?? s.contact_no ?? "";
}

function sitePayload(form) {
  return {
    name: form.name.trim(),
    address: (form.address || "").trim(),
    contact: (form.contact || "").trim(),
    is_active: Boolean(form.is_active),
  };
}

export default function RmcSalesParties() {
  const qc = useQueryClient();
  const [partyDrawerOpen, setPartyDrawerOpen] = React.useState(false);
  const [partyEditId, setPartyEditId] = React.useState(null);
  const [form, setForm] = React.useState(emptyPartyForm);

  const [sitesPartyId, setSitesPartyId] = React.useState(null);
  const [siteForm, setSiteForm] = React.useState(emptySiteForm);
  const [siteFormMode, setSiteFormMode] = React.useState("add");
  const [editingSiteId, setEditingSiteId] = React.useState(null);

  const [confirmDeletePartyId, setConfirmDeletePartyId] = React.useState(null);
  const [confirmDeleteSite, setConfirmDeleteSite] = React.useState(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });

  const openCreateParty = () => {
    setPartyEditId(null);
    setForm(emptyPartyForm());
    setPartyDrawerOpen(true);
  };

  const openEditParty = (p) => {
    setPartyEditId(p.id);
    setForm({
      name: p.name || "",
      gst_number: (p.gst_number || "").toUpperCase(),
      address: p.address || "",
      contact_no: p.contact_no ?? "",
      is_active: p.is_active !== false,
    });
    setPartyDrawerOpen(true);
  };

  const closePartyDrawer = () => {
    setPartyDrawerOpen(false);
    setPartyEditId(null);
    setForm(emptyPartyForm());
  };

  const saveParty = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        address: form.address.trim(),
        gst_number: form.gst_number.trim().toUpperCase(),
        contact_no: form.contact_no.trim(),
        is_active: Boolean(form.is_active),
      };
      if (partyEditId) return rmcClient.updateSalesParty(partyEditId, body);
      return rmcClient.createSalesParty(body);
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["salesParties"] });
      closePartyDrawer();
      toast.success(partyEditId ? "Party updated" : "Party created");
      const created = res?.data;
      if (!partyEditId && created?.id) {
        setSitesPartyId(created.id);
        toast("Add at least one delivery site for this party before using it on challans.", {
          duration: 5000,
        });
      }
    },
    onError: () =>
      toast.error(partyEditId ? "Failed to update party." : "Failed to create party."),
  });

  const removeParty = useMutation({
    mutationFn: (id) => rmcClient.deleteSalesParty(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["salesParties"] });
      setConfirmDeletePartyId(null);
      setSitesPartyId((sid) => (sid === id ? null : sid));
      toast.success("Party deleted");
    },
    onError: () => toast.error("Failed to delete party."),
  });

  const addSite = useMutation({
    mutationFn: ({ pid, payload }) => rmcClient.addSalesSite(pid, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesParties"] });
      setSiteForm(emptySiteForm());
      setSiteFormMode("add");
      setEditingSiteId(null);
      toast.success("Site added");
    },
    onError: () => toast.error("Failed to add site."),
  });

  const updateSite = useMutation({
    mutationFn: ({ pid, sid, payload }) => rmcClient.updateSalesSite(pid, sid, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesParties"] });
      setSiteForm(emptySiteForm());
      setSiteFormMode("add");
      setEditingSiteId(null);
      toast.success("Site updated");
    },
    onError: () => toast.error("Failed to update site."),
  });

  const removeSite = useMutation({
    mutationFn: ({ pid, sid }) => rmcClient.deleteSalesSite(pid, sid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesParties"] });
      setConfirmDeleteSite(null);
      toast.success("Site deleted");
    },
    onError: () => toast.error("Failed to delete site."),
  });

  const sitesDrawerParty = React.useMemo(
    () => (sitesPartyId != null ? rows.find((p) => p.id === sitesPartyId) ?? null : null),
    [sitesPartyId, rows]
  );

  React.useEffect(() => {
    if (sitesPartyId == null) {
      setSiteForm(emptySiteForm());
      setSiteFormMode("add");
      setEditingSiteId(null);
    }
  }, [sitesPartyId]);

  const openSitesDrawer = (party) => setSitesPartyId(party.id);

  const sitesList = sitesDrawerParty?.sites || [];
  const siteCount = (p) => (p.sites || []).length;
  const partyName = form.name.trim();
  const partyGst = form.gst_number.trim();
  const isPartyFormValid =
    partyName.length > 0 && (!partyGst || gstSchema.safeParse(partyGst).success);

  const beginEditSite = (s) => {
    setSiteFormMode("edit");
    setEditingSiteId(s.id);
    setSiteForm({
      name: s.name || "",
      address: s.address || "",
      contact: siteContact(s),
      is_active: s.is_active !== false,
    });
  };

  const cancelSiteEdit = () => {
    setSiteForm(emptySiteForm());
    setSiteFormMode("add");
    setEditingSiteId(null);
  };

  const submitSiteForm = () => {
    if (!sitesDrawerParty) return;
    const payload = sitePayload(siteForm);
    if (!payload.name) {
      toast.error("Site name is required");
      return;
    }
    if (siteFormMode === "edit" && editingSiteId != null) {
      updateSite.mutate({
        pid: sitesDrawerParty.id,
        sid: editingSiteId,
        payload,
      });
      return;
    }
    addSite.mutate({ pid: sitesDrawerParty.id, payload });
  };

  const siteBusy = addSite.isPending || updateSite.isPending;

  return (
    <div className="space-y-6">
      <PageBreadCrumb pageTitle="Sales Party Master & Sites" />
      <div className="flex justify-end">
        <Button size="sm" onClick={openCreateParty}>
          Add party
        </Button>
      </div>

      {isLoading ? (
        <div className="rounded-xl border border-gray-800 bg-gray-950/40 px-6 py-12 text-center text-sm text-gray-500">
          Loading parties…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-700 bg-gray-950/30 px-6 py-14 text-center">
          <p className="text-sm font-medium text-gray-300">No sales parties yet</p>
          <p className="mt-1 text-xs text-gray-500 max-w-md mx-auto">
            Create a party with a valid GST number, then add one or more delivery sites for that party.
          </p>
          <Button size="sm" className="mt-4" onClick={openCreateParty}>
            Add first party
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {rows.map((p) => {
            const sites = p.sites || [];
            const preview = sites.slice(0, PREVIEW_SITES);
            const more = sites.length - preview.length;

            return (
              <div
                key={p.id}
                className="flex flex-col rounded-xl border border-gray-800 bg-gray-950/40 shadow-theme-sm overflow-hidden"
              >
                <div className="border-b border-gray-800/80 bg-gray-950/80 px-4 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-white truncate">{p.name}</h3>
                        {p.is_active === false ? (
                          <Badge size="sm" variant="light" color="warning">
                            Inactive
                          </Badge>
                        ) : null}
                      </div>
                      {p.gst_number ? (
                        <p className="mt-0.5 font-mono text-[11px] text-gray-500">{p.gst_number}</p>
                      ) : null}
                      {p.contact_no ? (
                        <p className="mt-0.5 text-[11px] text-gray-400">Contact: {p.contact_no}</p>
                      ) : null}
                      {p.address ? (
                        <p className="mt-1 text-[11px] leading-snug text-gray-500 line-clamp-2">
                          {p.address}
                        </p>
                      ) : null}
                    </div>
                    <Badge size="sm" variant="light" color="primary">
                      {siteCount(p)} site{siteCount(p) === 1 ? "" : "s"}
                    </Badge>
                  </div>
                </div>

                <div className="flex flex-1 flex-col px-4 py-3">
                  {sites.length === 0 ? (
                    <p className="text-xs text-gray-500 italic py-2">
                      No delivery sites — add sites before using this party on challans.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                        Delivery sites
                      </p>
                      <ul className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                        {preview.map((s) => (
                          <li
                            key={s.id}
                            className="flex flex-col gap-0.5 rounded-md border border-gray-800/80 bg-gray-900/50 px-2.5 py-1.5 text-xs text-gray-200"
                          >
                            <span className="flex items-center gap-2">
                              <span className="text-gray-600">▸</span>
                              <span className="truncate font-medium">{s.name}</span>
                            </span>
                            {siteContact(s) ? (
                              <span className="pl-5 text-[10px] text-gray-500">{siteContact(s)}</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                      {more > 0 && (
                        <p className="text-[11px] text-gray-500">
                          +{more} more — open manage to see all
                        </p>
                      )}
                    </div>
                  )}
                  <div className="mt-auto flex flex-wrap gap-2 border-t border-gray-800/80 pt-3">
                    <Button type="button" size="sm" variant="outline" onClick={() => openEditParty(p)}>
                      Edit party
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => openSitesDrawer(p)}>
                      Manage sites
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-red-900/60 text-red-400 hover:bg-red-950/40 hover:text-red-300"
                      startIcon={
                        <svg viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5" aria-hidden="true">
                          <path
                            d="M7.5 3.5h5M4.5 5.5h11M6.5 5.5l.6 9a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-9M8.5 8.5v4.5M11.5 8.5v4.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      }
                      onClick={() => setConfirmDeletePartyId(p.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DrawerPanel
        open={partyDrawerOpen}
        onClose={closePartyDrawer}
        title={partyEditId ? "Edit sales party" : "New sales party"}
        widthClass="max-w-md"
      >
        <div className="space-y-3">
          <div>
            <Label>Party name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1"
              placeholder="e.g. ABC Infra Pvt Ltd"
            />
          </div>
          <div>
            <Label>GST number</Label>
            <Input
              value={form.gst_number}
              onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })}
              className="mt-1 font-mono"
              placeholder="24AAAAA0000A1Z5"
            />
          </div>
          <div>
            <Label>Address</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mt-1"
              placeholder="Ring Road, Surat"
            />
          </div>
          <div>
            <Label>Contact no.</Label>
            <Input
              value={form.contact_no}
              onChange={(e) => setForm({ ...form, contact_no: e.target.value })}
              className="mt-1"
              placeholder="9999999999"
            />
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <div className="grid grid-cols-2 gap-2">
              <label
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-3 text-sm font-medium transition ${
                  form.is_active
                    ? "menu-dropdown-badge-active border-white/70 text-white"
                    : "border-gray-700 text-gray-300 hover:border-gray-500"
                }`}
              >
                <input
                  type="radio"
                  name="partyStatus"
                  checked={form.is_active}
                  onChange={() => setForm({ ...form, is_active: true })}
                  className="h-4 w-4"
                />
                Active
              </label>
              <label
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-3 text-sm font-medium transition ${
                  !form.is_active
                    ? "menu-dropdown-badge-active border-white/70 text-white"
                    : "border-gray-700 text-gray-300 hover:border-gray-500"
                }`}
              >
                <input
                  type="radio"
                  name="partyStatus"
                  checked={!form.is_active}
                  onChange={() => setForm({ ...form, is_active: false })}
                  className="h-4 w-4"
                />
                Inactive
              </label>
            </div>
          </div>
          <Button
            size="md"
            className="mt-2 w-full"
            disabled={saveParty.isPending || !isPartyFormValid}
            onClick={() => {
              if (!partyName) {
                toast.error("Party name is required");
                return;
              }
              if (partyGst && !gstSchema.safeParse(partyGst).success) {
                toast.error("Invalid GST");
                return;
              }
              saveParty.mutate();
            }}
          >
            {saveParty.isPending
              ? partyEditId
                ? "Updating…"
                : "Saving…"
              : partyEditId
                ? "Update party"
                : "Save party"}
          </Button>
        </div>
      </DrawerPanel>

      <DrawerPanel
        open={sitesPartyId != null}
        onClose={() => {
          setSitesPartyId(null);
          cancelSiteEdit();
        }}
        title={sitesDrawerParty ? `Sites — ${sitesDrawerParty.name}` : "Manage sites"}
        widthClass="max-w-2xl"
      >
        {sitesPartyId != null && !sitesDrawerParty && (
          <p className="py-10 text-center text-sm text-gray-500">
            {isLoading ? "Loading…" : "Party not found in list. Close and reopen."}
          </p>
        )}
        {sitesPartyId != null && sitesDrawerParty && (
          <div className="space-y-5">
            <div className="rounded-lg border border-gray-800 bg-gray-900/40 px-3 py-2 text-[11px] text-gray-500">
              {sitesDrawerParty.gst_number ? (
                <span className="font-mono text-gray-400">{sitesDrawerParty.gst_number}</span>
              ) : (
                <span>No GST on file</span>
              )}
            </div>

            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                All sites ({sitesList.length})
              </p>
              <div className="overflow-x-auto overflow-hidden rounded-lg border border-gray-800">
                {sitesList.length === 0 ? (
                  <p className="px-4 py-8 text-center text-xs text-gray-500">
                    No sites yet. Add the first delivery location below.
                  </p>
                ) : (
                  <table className="w-full min-w-[520px] text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-800 bg-gray-950/90">
                        <th className="w-8 px-2 py-2 font-semibold text-gray-500">#</th>
                        <th className="px-2 py-2 font-semibold text-gray-500">Site</th>
                        <th className="px-2 py-2 font-semibold text-gray-500">Contact</th>
                        <th className="px-2 py-2 font-semibold text-gray-500">Address</th>
                        <th className="px-2 py-2 font-semibold text-gray-500">Active</th>
                        <th className="w-28 px-2 py-2 text-right font-semibold text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {sitesList.map((s, i) => (
                        <tr key={s.id} className="bg-gray-950/30">
                          <td className="px-2 py-2 text-gray-500">{i + 1}</td>
                          <td className="px-2 py-2 font-medium text-gray-200">{s.name}</td>
                          <td className="px-2 py-2 text-gray-400">{siteContact(s) || "—"}</td>
                          <td className="max-w-[140px] truncate px-2 py-2 text-gray-400">
                            {s.address?.trim() ? s.address : "—"}
                          </td>
                          <td className="px-2 py-2 text-gray-400">
                            {s.is_active === false ? "No" : "Yes"}
                          </td>
                          <td className="px-2 py-2 text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="px-2!"
                                onClick={() => beginEditSite(s)}
                              >
                                Edit
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="border-red-900/60 px-2! text-red-400"
                                onClick={() =>
                                  setConfirmDeleteSite({
                                    partyId: sitesDrawerParty.id,
                                    siteId: s.id,
                                    name: s.name,
                                  })
                                }
                              >
                                Del
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-gray-800 bg-gray-950/50 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                {siteFormMode === "edit" ? "Edit site" : "Add delivery site"}
              </p>
              <div>
                <Label htmlFor="site-name">Site name</Label>
                <Input
                  id="site-name"
                  placeholder="Main Plant Site"
                  value={siteForm.name}
                  onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="site-address">Address</Label>
                <Input
                  id="site-address"
                  placeholder="Hazira Road, Surat"
                  value={siteForm.address}
                  onChange={(e) => setSiteForm({ ...siteForm, address: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="site-contact">Contact</Label>
                <Input
                  id="site-contact"
                  placeholder="8888888888"
                  value={siteForm.contact}
                  onChange={(e) => setSiteForm({ ...siteForm, contact: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="grid grid-cols-2 gap-2">
                  <label
                    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-3 text-sm font-medium transition ${
                      siteForm.is_active
                        ? "menu-dropdown-badge-active border-white/70 text-white"
                        : "border-gray-700 text-gray-300 hover:border-gray-500"
                    }`}
                  >
                    <input
                      type="radio"
                      name="siteStatus"
                      checked={siteForm.is_active}
                      onChange={() => setSiteForm({ ...siteForm, is_active: true })}
                      className="h-4 w-4"
                    />
                    Active
                  </label>
                  <label
                    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-3 text-sm font-medium transition ${
                      !siteForm.is_active
                        ? "menu-dropdown-badge-active border-white/70 text-white"
                        : "border-gray-700 text-gray-300 hover:border-gray-500"
                    }`}
                  >
                    <input
                      type="radio"
                      name="siteStatus"
                      checked={!siteForm.is_active}
                      onChange={() => setSiteForm({ ...siteForm, is_active: false })}
                      className="h-4 w-4"
                    />
                    Inactive
                  </label>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {siteFormMode === "edit" ? (
                  <>
                    <Button type="button" size="sm" variant="outline" onClick={cancelSiteEdit}>
                      Cancel
                    </Button>
                    <Button type="button" size="sm" disabled={siteBusy} onClick={submitSiteForm}>
                      {updateSite.isPending ? "Updating…" : "Update site"}
                    </Button>
                  </>
                ) : (
                  <Button type="button" size="sm" disabled={siteBusy} onClick={submitSiteForm}>
                    {addSite.isPending ? "Adding…" : "Add site"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </DrawerPanel>

      <Modal
        isOpen={confirmDeletePartyId != null}
        onClose={() => {
          if (!removeParty.isPending) setConfirmDeletePartyId(null);
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete sales party?</h3>
          <p className="text-sm leading-relaxed text-gray-400">
            This removes the party and its site links. Sales history may still reference old challans.
            Continue?
          </p>
          <div className="flex flex-wrap justify-end gap-2 border-t border-gray-800 pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={removeParty.isPending}
              onClick={() => setConfirmDeletePartyId(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={removeParty.isPending}
              onClick={() => confirmDeletePartyId != null && removeParty.mutate(confirmDeletePartyId)}
            >
              {removeParty.isPending ? "Deleting…" : "Delete party"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={confirmDeleteSite != null}
        onClose={() => {
          if (!removeSite.isPending) setConfirmDeleteSite(null);
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete site?</h3>
          <p className="text-sm leading-relaxed text-gray-400">
            Remove <span className="font-medium text-gray-200">{confirmDeleteSite?.name}</span> from
            this party?
          </p>
          <div className="flex flex-wrap justify-end gap-2 border-t border-gray-800 pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={removeSite.isPending}
              onClick={() => setConfirmDeleteSite(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={removeSite.isPending}
              onClick={() => {
                if (!confirmDeleteSite) return;
                removeSite.mutate({
                  pid: confirmDeleteSite.partyId,
                  sid: confirmDeleteSite.siteId,
                });
              }}
            >
              {removeSite.isPending ? "Deleting…" : "Delete site"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
